import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

interface PlanQuestion {
  number: number;
  text: string;
  approach: string;
}

interface ProposalPlan {
  questions: PlanQuestion[];
  strategy: string;
  estimatedCost: string;
}

interface PlanApprovalDialogProps {
  open: boolean;
  plan: ProposalPlan | null;
  planningCost: number;
  rfpName: string;
  onApprove: (modifiedPlan?: ProposalPlan) => void;
  onReject: () => void;
  onSavePlan?: () => void;
}

const PlanApprovalDialog: React.FC<PlanApprovalDialogProps> = ({
  open,
  plan,
  planningCost,
  rfpName,
  onApprove,
  onReject,
  onSavePlan
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!plan) return null;

  const handleApprove = async () => {
    setLoading(true);
    onApprove();
  };

  const handleReject = () => {
    setLoading(true);
    onReject();
  };

  const handleSave = async () => {
    if (onSavePlan) {
      setSaving(true);
      onSavePlan();
      // Note: Dialog will close via parent component
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown  // Prevent accidental dismissal
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <EditIcon color="primary" />
          <Typography variant="h6">
            Review Proposal Generation Plan
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {rfpName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Cost Information */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Planning Cost:</strong> ${planningCost.toFixed(4)}
              {' | '}
              <strong>Estimated Full Generation Cost:</strong> {plan.estimatedCost}
            </Typography>
          </Alert>

          {/* Overall Strategy */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Overall Strategy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {plan.strategy}
            </Typography>
          </Box>

          <Divider />

          {/* Questions List */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Questions to Answer
              </Typography>
              <Chip label={`${plan.questions.length} questions`} size="small" />
            </Stack>

            <List dense>
              {plan.questions.map((question, index) => (
                <React.Fragment key={question.number}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {question.number}. {question.text}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Approach:</strong> {question.approach}
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>

          {/* Warning */}
          <Alert severity="warning">
            <Typography variant="body2">
              Approving this plan will proceed with full proposal generation (~{plan.estimatedCost}).
              If the plan doesn't match your expectations, reject it to save costs.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleReject}
          disabled={loading || saving}
          startIcon={<CancelIcon />}
          color="error"
          variant="outlined"
        >
          Reject Plan
        </Button>
        <Box sx={{ flex: 1 }} />
        {onSavePlan && (
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            color="inherit"
            variant="outlined"
          >
            {saving ? 'Saving...' : 'Save for Later'}
          </Button>
        )}
        <Button
          onClick={handleApprove}
          disabled={loading || saving}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          color="primary"
          variant="contained"
          autoFocus
        >
          {loading ? 'Approving...' : 'Approve & Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanApprovalDialog;
